import React from "react";
import { Link } from "react-router-dom";
import {
  ArrowRight,
  Quote,
  Play,
} from "lucide-react";
import useReveal from "../hooks/useReveal";
import useDocMeta from "../hooks/useDocMeta";
import HeroCover from "../components/HeroCover";
import ContactSection from "../components/ContactSection";
import ReviewsSection from "../components/ReviewsSection";
import HomeFAQ from "../components/HomeFAQ";
import {
  HOME,
  IMAGES,
  META,
} from "../mock";

export default function Home() {
  useReveal();
  useDocMeta(META.home);
  const [videoOn, setVideoOn] = React.useState(false);

  return (
    <div>
      {/* HERO - full cover with enquiry form (includes accreditation ribbon) */}
      <HeroCover badges={[]} />

      {/* WHAT IS TMS */}
      <section className="py-12 sm:py-16 bg-sand-50">
        <div className="max-w-4xl mx-auto px-5 sm:px-8 text-center reveal">
          <h2 className="font-display text-3xl sm:text-4xl text-brand-900 text-balance">
            {HOME.whatIsTitle}
          </h2>
          {HOME.whatIsIntro && (
            <p className="mt-5 text-lg text-brand-900/70 leading-relaxed">{HOME.whatIsIntro}</p>
          )}
        </div>
        <div className="max-w-6xl mx-auto px-5 sm:px-8 mt-12 grid lg:grid-cols-2 gap-10 lg:gap-14 items-start">
          <div className="reveal lg:sticky lg:top-28">
            <div className="rounded-[2rem] overflow-hidden shadow-[0_30px_70px_-40px_rgba(31,56,53,0.5)] aspect-[4/3]">
              <img
                src={IMAGES.heroClinic}
                alt="Transcranial Magnetic Stimulation (TMS) treatment at AwareHub"
                className="h-full w-full object-cover"
              />
            </div>
          </div>
          <ul className="space-y-4">
            {HOME.whatIsParagraphs.map((p, i) => (
              <li key={i} className="flex gap-3 text-brand-900/75 leading-relaxed text-[1.02rem]">
                <span className="mt-2.5 h-1.5 w-1.5 rounded-full bg-brand-500 shrink-0" />
                <span>{p}</span>
              </li>
            ))}
          </ul>
        </div>
      </section>

      {/* VIDEO (large, full row) */}
      <section className="py-12 sm:py-16 bg-white">
        <div className="max-w-[1400px] mx-auto px-5 sm:px-8">
          <div className="text-center reveal mb-10 max-w-3xl mx-auto">
            <h2 className="font-display text-3xl sm:text-4xl text-brand-900">
              {HOME.videoCaptionTitle}
            </h2>
            <p className="mt-5 text-lg text-brand-900/70 leading-relaxed">{HOME.videoCaptionBody}</p>
          </div>
          <div className="reveal rounded-[2rem] overflow-hidden shadow-[0_50px_110px_-50px_rgba(31,56,53,0.6)] aspect-video relative bg-brand-950" data-testid="home-video">
            {videoOn ? (
              <iframe
                className="absolute inset-0 h-full w-full"
                src={`https://www.youtube.com/embed/${HOME.videoId}?autoplay=1`}
                title="Transcranial magnetic stimulation (TMS) - AwareHub Wellness Clinic"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                allowFullScreen
              />
            ) : (
              <button
                onClick={() => setVideoOn(true)}
                className="group absolute inset-0 h-full w-full"
                aria-label="Play video"
                data-testid="home-video-play-button"
              >
                <img
                  src={`https://img.youtube.com/vi/${HOME.videoId}/maxresdefault.jpg`}
                  alt="Transcranial magnetic stimulation (TMS) - AwareHub Wellness Clinic"
                  className="h-full w-full object-cover opacity-85 group-hover:opacity-100 transition-opacity"
                />
                <span className="absolute inset-0 flex items-center justify-center">
                  <span className="h-28 w-28 rounded-full bg-white/90 group-hover:bg-white flex items-center justify-center shadow-2xl transition-transform group-hover:scale-110">
                    <Play className="h-11 w-11 text-brand-700 ml-1" fill="currentColor" />
                  </span>
                </span>
              </button>
            )}
          </div>
        </div>
      </section>

      {/* REVOLUTIONARY / CARDS */}
      <section className="py-12 sm:py-16 bg-brand-50">
        <div className="max-w-7xl mx-auto px-5 sm:px-8">
          <h2 className="font-display text-3xl sm:text-4xl text-brand-900 text-center mb-10 reveal text-balance">
            {HOME.revolutionaryTitle}
          </h2>
          <div className="space-y-12">
            {HOME.revolutionaryCards.map((card, i) => (
              <div
                key={i}
                className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-center reveal ${
                  i % 2 === 1 ? "lg:[&>div:first-child]:order-2" : ""
                }`}
              >
                <div className="rounded-[2rem] overflow-hidden shadow-[0_30px_70px_-40px_rgba(34,63,60,0.45)] aspect-[16/11] ring-1 ring-brand-100">
                  <img src={card.image} alt={card.title} className="h-full w-full object-cover" />
                </div>
                <div className="rounded-[2rem] bg-gradient-to-br from-brand-100 to-brand-50 border border-brand-200 p-8 sm:p-10">
                  <h3 className="font-display text-2xl sm:text-3xl text-brand-700 mb-4">
                    {card.title}
                  </h3>
                  <p className="text-brand-900/70 leading-relaxed">{card.body}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* QUOTE + ENQUIRE */}
      <section className="relative overflow-hidden py-16 sm:py-24">
        <img src={IMAGES.ocean} alt="Calm ocean" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-brand-950/80" />
        <div className="relative max-w-4xl mx-auto px-5 sm:px-8 text-center reveal">
          <Quote className="h-10 w-10 text-brand-300 mx-auto" />
          <p className="mt-6 font-display text-2xl sm:text-3xl lg:text-4xl text-white leading-snug text-balance italic">
            {HOME.quote}
          </p>
          <p className="mt-6 text-brand-200 tracking-wide">{HOME.quoteAuthor}</p>
          <div className="mt-12 max-w-2xl mx-auto rounded-3xl bg-white/10 backdrop-blur-sm border border-white/15 p-8 text-center">
            <h3 className="font-display text-xl sm:text-2xl text-white mb-6">{HOME.enquireTitle}</h3>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 rounded-full bg-white text-brand-800 hover:bg-brand-50 px-6 py-3 font-semibold transition-colors"
            >
              Find out more <ArrowRight className="h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>

      {/* GOOGLE REVIEWS */}
      <ReviewsSection />

      {/* FAQ — teal creative block */}
      <HomeFAQ />

      <ContactSection />
    </div>
  );
}
